import { useStores } from "@sushicorp/contexts";
import { useCatalogues } from "@sushicorp/contexts";
import { useVendors } from "@sushicorp/contexts";
import { useFetchCategoriesWithProducts } from "@sushicorp/services";
import { sanitizeQueryParams, scrollToElement } from "@sushicorp/utils";
import { toSentenceCaseUtility } from "@sushicorp/utils";
import { Category } from "artisn/types";
import { InfiniteScroll } from "artisn-ui-react";
import { ScrollToTop } from "artisn-ui-react";
import { useRouter } from "next/router";
import React, { useMemo, useState, useEffect, useCallback } from "react";

import Styles from "./Categories.styles";
import { CategoriesProps as Props } from "./Categories.types";
import CategoriesTabsMobile from "components/global/CategoriesTabsMobile/CategoriesTabsMobile";
import CategoryList from "components/global/CategoryList/CategoryList";
import Footer from "components/global/Footer/Footer";
import Navbar from "components/global/Navbar/Navbar";
import ProductCard from "components/global/ProductCard/ProductCard";
import ProductGridPlaceholder from "components/global/ProductGridPlaceholder/ProductGridPlaceholder";
import CONSTANTS from "config/constants";
import { useTalkShop } from "contexts/talkShop/talkShop.context.hooks";
import useRequestNavigatorGeo from "hooks/useRequestNavigatorGeo";
import useWindowSize from "hooks/useWindowSize";
import { notify } from "utils/common.utils";

import ArrowLeftSVG from "../../../../public/assets/images/arrow-left.svg";

const { FEATURE_FLAGS, BREAKPOINTS } = CONSTANTS;
const { WITH_TALK_SHOP, WITH_BOTTOM_SEARCH } = FEATURE_FLAGS;
const { WITH_VERTICAL_CATEGORIES } = FEATURE_FLAGS;
const { WITH_BOTTOM_RESPONSIVE_BUTTONS } = FEATURE_FLAGS;
const { tablet } = BREAKPOINTS;

const Categories: React.FC<Props> = props => {
  const { selectedVendorId } = useVendors();
  const { selectedCatalogueId } = useCatalogues();
  const { selectedStore } = useStores();
  const { query } = useRouter();
  const { storeId } = selectedStore ?? {};
  const [selectedCategory, setSelectedCategory] = useState<Category>();
  const { talkShopProvider, talkShopIdentifier } = useTalkShop();
  const categoryResponse = useFetchCategoriesWithProducts(
    {
      catalogueId: selectedCatalogueId,
      storeId: storeId!,
      vendorId: selectedVendorId,
      page: 1
    },
    !!storeId,
    true,
    notify
  );
  const { categoryId: categoryParamId } = sanitizeQueryParams(query);
  const { width } = useWindowSize();
  const isMobile = width <= tablet;
  const talkShop = !!talkShopProvider && !!talkShopIdentifier && WITH_TALK_SHOP;
  const { data: categoriesPages, fetchNextPage, isLoading } = categoryResponse;
  const { isFetching, hasNextPage } = categoryResponse;

  useRequestNavigatorGeo();

  /**
   * Handler when executing the click event in the category.
   *
   * @since 1.0.0
   * @param {Category} clickedCategory Category data.
   */
  const categoryClickHandler = (clickedCategory: Category) => {
    setSelectedCategory(clickedCategory);
  };

  const categoriesArray = useMemo(() => {
    return categoriesPages?.pages.flatMap(page => page.data);
  }, [categoriesPages]);

  /**
   * Load more products to display
   *
   * @since 1.0.0
   */
  const loadMoreProducts = useCallback(() => {
    if (!hasNextPage) return;
    fetchNextPage();
  }, [fetchNextPage, hasNextPage]);

  useEffect(() => {
    if (!selectedCategory || !categoriesArray) return;
    const { categoryId } = selectedCategory;
    const offset = -180;
    // TODO: review later
    // const offset = isMobile ? -180 : -144;
    const isFind = categoriesArray.some(
      category => category.categoryId === categoryId
    );
    if (!isFind) {
      loadMoreProducts();
      return;
    }
    scrollToElement(`${categoryId}`, false, offset);
  }, [selectedCategory, categoriesArray, loadMoreProducts]);

  useEffect(() => {
    if (!categoryParamId || !categoriesArray?.length) return;
    const isFind = categoriesArray.some(
      category => `${category.categoryId}` === categoryParamId
    );
    if (!isFind) {
      loadMoreProducts();
      return;
    }
    const offset = isMobile ? -180 : -200;
    setTimeout(() => {
      scrollToElement(`${categoryParamId}`, false, offset);
    }, 2000);
  }, [categoriesArray, categoryParamId, isMobile, loadMoreProducts]);

  return (
    <Styles
      className="Categories"
      talkShop={talkShop}
      withBottomSearch={WITH_BOTTOM_SEARCH}
      withBottomResponsiveButtons={WITH_BOTTOM_RESPONSIVE_BUTTONS}
    >
      <header className="Categories__header">
        <Navbar title="Categorías" showBrand to="/" />
      </header>
      <section className="Categories__categoriesContainer">
        <CategoriesTabsMobile
          className="Categories__tabs-mobile Categories__hide-scroll"
          onClickCategoryItem={categoryClickHandler}
          withScrollTo
        />
      </section>
      <main className="Categories__main">
        <div className="Categories__main__content">
          {WITH_VERTICAL_CATEGORIES ? (
            <CategoryList
              className="Categories__category__list"
              onClickCategoryItem={categoryClickHandler}
              withScrollTo
            />
          ) : null}
          <section className="Categories__scrollGalleryContainer">
            <InfiniteScroll
              className="Categories__scroll Categories__scroll"
              onEndReached={loadMoreProducts}
              disconnected={false}
            >
              {categoriesArray?.map(category => {
                const { products, categoryId, name } = category;
                return (
                  <div
                    key={`${categoryId}-${name}`}
                    id={`${categoryId}`}
                    className="Categories__category"
                  >
                    <p className="Categories__category__name">
                      {toSentenceCaseUtility(name)}
                    </p>
                    <div className="Categories__category__products">
                      {products.map(product => {
                        const { productId } = product;
                        return (
                          <ProductCard
                            key={`${productId}-${categoryId}`}
                            product={product}
                            className="Categories__category__product"
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </InfiniteScroll>
            {isLoading || isFetching ? (
              <ProductGridPlaceholder
                className="Categories__product-grid-placeholder"
                quantity={6}
              />
            ) : null}
          </section>
          <ScrollToTop className="Categories__scrollToTopButton" screen={0.5}>
            <ArrowLeftSVG />
          </ScrollToTop>
        </div>
      </main>
      <footer className="Categories__footer">
        <Footer />
      </footer>
    </Styles>
  );
};

Categories.defaultProps = {};

export default Categories;
